import React from 'react';
import styles from './FullTermsModal.module.css';

const FullTermsModal = ({ isOpen, onClose, type = 'terms' }) => {
  if (!isOpen) return null;

  const isTerms = type === 'terms';
  const title = isTerms ? 'Trakvu Terms of Service' : 'Trakvu Privacy Policy';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{title}</h2>
          <button className={styles.closeButton} onClick={onClose} type="button">
            <span>&times;</span>
          </button>
        </div>

        <div className={styles.modalBody}>
          {isTerms ? <TermsContent /> : <PrivacyContent />}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.closeBtn}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const TermsContent = () => (
  <div className={styles.contentSection}>
    <p className={styles.lastUpdated}>Last Updated: June 2025</p>

    <section className={styles.section}>
      <h3>1. Acceptance of Terms</h3>
      <p>
        By accessing or using Trakvu.com ("Service"), a product of SmartTech ProServe LLC ("Company," "we," "our," or "us"), including all associated applications, features, content, and services, you ("you" or "User") agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. If you do not agree to these Terms, do not access or use the Service.
      </p>
    </section>

    <section className={styles.section}>
      <h3>2. Scope and Application</h3>
      <p>
        These Terms apply to all users, whether individuals, businesses, or institutions. By using Trakvu, you represent that you are at least 18 years old and have the legal authority to accept these Terms on behalf of yourself or your organization.
      </p>
    </section>

    <section className={styles.section}>
      <h3>3. Generative AI Disclosure and Use of Input/Output</h3>
      
      <h4>3.1 AI Use:</h4>
      <p>
        Trakvu uses Generative AI to analyze contractor proposals and generate project insights. You acknowledge that outputs may be based on statistical inference, and are not guaranteed to be accurate, complete, or reliable.
      </p>

      <h4>3.2 Input Ownership and Use:</h4>
      <p>
        You retain ownership of any content, data, or documents you submit ("User Content"). However, by submitting User Content, you grant us and our affiliates, contractors, employees, and technology partners a non-exclusive, worldwide, royalty-free, irrevocable license to use, copy, process, modify, publish, and analyze such content to:
      </p>
      <ul>
        <li>Provide you with services</li>
        <li>Train and improve our AI models</li>
        <li>Enhance platform features</li>
        <li>Aggregate for anonymous benchmarking and insights</li>
      </ul>

      <h4>3.3 AI Output Use:</h4>
      <p>
        Generated outputs may be stored, reviewed, and analyzed to improve performance. No compensation is owed to users for the use of their content or feedback.
      </p>

      <h4>3.4 Third-Party AI Providers:</h4>
      <p>
        We may transmit data to third-party AI vendors (e.g., OpenAI) in compliance with their respective privacy and use policies. You acknowledge that these providers may process inputs to improve their services.
      </p>

      <h4>3.5 AI Data Use Transparency and Opt-Out Options</h4>
      <p>
        To support continuous improvement of our AI systems, we may use anonymized user inputs and outputs to enhance training, accuracy, and performance. If you prefer not to have your data used in this way, you may opt out by:
      </p>
      <ul>
        <li>Adjusting your settings in the user dashboard under "AI Preferences," or</li>
        <li>Emailing us at privacy@trakvu.com with your opt-out request.</li>
      </ul>
      <p>
        Note: Certain AI-powered features may be limited or disabled if you opt out. Regardless of opt-out, we will always use your data as needed to deliver core services and meet legal obligations.
      </p>
    </section>

    <section className={styles.section}>
      <h3>4. Regional Access Restrictions</h3>
      <p>
        Access to Trakvu is prohibited for users located in jurisdictions that impose unacceptable legal or operational risks, including the People's Republic of China. We may geo-block access based on IP address or location data.
      </p>
    </section>

    <section className={styles.section}>
      <h3>5. Use Restrictions</h3>
      <p>You may not use the Service to:</p>
      <ul>
        <li>Violate any laws or regulations</li>
        <li>Generate defamatory, discriminatory, misleading, or harmful content</li>
        <li>Reverse engineer, scrape, or extract source code or data</li>
        <li>Train competing AI models or systems</li>
        <li>Circumvent geographic restrictions</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h3>6. Data Privacy and Cross-Border Transfers</h3>
      <p>
        We process data in accordance with applicable data privacy laws including GDPR, CCPA/CPRA, and Quebec Law 25. By using our services, you consent to:
      </p>
      <ul>
        <li>The transfer of personal data to the United States and third-party processors</li>
        <li>Our use of Standard Contractual Clauses (SCCs) where required</li>
        <li>Receiving required notices via in-product pop-ups or dashboards</li>
      </ul>
      <p>
        We discourage submitting personal, financial, or health-related information unless required by your project.
      </p>
    </section>

    <section className={styles.section}>
      <h3>7. Cookies and Tracking Technologies</h3>
      <p>
        We use cookies to enable core functionality and gather usage analytics. Users in jurisdictions requiring consent (e.g., EU, UK) will be presented with cookie notices and management tools.
      </p>
    </section>

    <section className={styles.section}>
      <h3>8. Fees, Payment, and Taxes</h3>
      <p>
        Fees are outlined at checkout or in custom agreements. All fees are payable in advance and non-refundable. Late payments may incur 1.5% monthly interest. You are responsible for all applicable taxes.
      </p>
    </section>

    <section className={styles.section}>
      <h3>9. Suspension and Termination</h3>
      <p>We may suspend or terminate your access without notice if you:</p>
      <ul>
        <li>Violate these Terms</li>
        <li>Fail to pay due fees within 45 days</li>
        <li>Cause a security, legal, or reputational risk</li>
      </ul>
      <p>
        Users may terminate accounts at any time. Upon termination, access ends and user data may be deleted after 60 days unless retrieval is requested.
      </p>
    </section>

    <section className={styles.section}>
      <h3>10. Intellectual Property</h3>
      <p>
        All Trakvu software, designs, trademarks, documentation, and derived outputs remain the sole property of SmartTech ProServe LLC. You are granted a limited, revocable, non-transferable license to use the Service as described.
      </p>
    </section>

    <section className={styles.section}>
      <h3>11. Disclaimers and Limitation of Liability</h3>
      <ul>
        <li>The Service is provided "as is" without warranties of any kind</li>
        <li>We disclaim all liability for errors, omissions, delays, or inaccuracies in AI-generated content</li>
        <li>We are not liable for any indirect, incidental, or consequential damages</li>
        <li>Our total liability is limited to the fees paid by you in the three months prior to the claim</li>
      </ul>
      <p>
        AI-generated outputs including scopes of work, project execution checklists, draft statements of understanding, and draft contracts or agreements between users and contractors are provided for informational and educational purposes only. You are solely responsible for reviewing, modifying, and verifying the suitability and legality of such outputs prior to use. We disclaim any responsibility or liability for consequences resulting from your reliance on or use of such materials.
      </p>
    </section>

    <section className={styles.section}>
      <h3>12. Indemnification</h3>
      <p>
        You agree to indemnify, defend, and hold harmless Trakvu, SmartTech ProServe, Chantru, their owners, employees, contractors, affiliates, and partners from any claims arising from:
      </p>
      <ul>
        <li>Your use or misuse of the Service</li>
        <li>Violation of these Terms</li>
        <li>Infringement of third-party rights or laws</li>
      </ul>
      <p>
        We agree to indemnify you for IP infringement claims related to authorized use, subject to notice and control rights.
      </p>
    </section>

    <section className={styles.section}>
      <h3>13. Third-Party Tools and Integrations</h3>
      <p>
        Trakvu uses vendors such as OpenAI, Stripe, Paddle, and others. These third parties may have access to your data under their own terms. We are not liable for their performance or practices.
      </p>
    </section>

    <section className={styles.section}>
      <h3>14. Modifications and Notice</h3>
      <p>
        We may modify these Terms at any time. Changes will be posted on our website or notified via in-product alerts. Continued use constitutes acceptance.
      </p>
    </section>

    <section className={styles.section}>
      <h3>15. Governing Law and Dispute Resolution</h3>
      <p>
        These Terms are governed by the laws of the Commonwealth of Pennsylvania. Any disputes will be resolved via binding arbitration in Chester County, PA.
      </p>
    </section>

    <section className={styles.section}>
      <h3>16. Miscellaneous</h3>
      <ul>
        <li>You may not assign rights without consent</li>
        <li>Force majeure events excuse non-performance</li>
        <li>These Terms constitute the entire agreement</li>
        <li>Electronic signatures are valid and binding</li>
      </ul>
      <p>
        For questions, contact legal@trakvu.com.
      </p>
    </section>
  </div>
);

const PrivacyContent = () => (
  <div className={styles.contentSection}>
    <p className={styles.lastUpdated}>Last Updated: June 2025</p>

    <section className={styles.section}>
      <h3>1. Information We Collect</h3>
      <p>
        We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support. This includes personal information like your name, email address, phone number, and any content you submit through our platform.
      </p>
    </section>

    <section className={styles.section}>
      <h3>2. How We Use Your Information</h3>
      <p>
        We use the information we collect to:
      </p>
      <ul>
        <li>Provide, maintain, and improve our services</li>
        <li>Process transactions and send related information</li>
        <li>Send technical notices and support messages</li>
        <li>Communicate with you about products, services, and events</li>
        <li>Monitor and analyze trends and usage</li>
        <li>Detect, investigate, and prevent fraudulent activities</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h3>3. Information Sharing and Disclosure</h3>
      <p>
        We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
      </p>
      <ul>
        <li>With your explicit consent</li>
        <li>To comply with legal obligations</li>
        <li>To protect our rights and safety</li>
        <li>With service providers who assist us in operations</li>
        <li>In connection with a merger or acquisition</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h3>4. Data Security</h3>
      <p>
        We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
      </p>
    </section>

    <section className={styles.section}>
      <h3>5. Your Rights and Choices</h3>
      <p>
        You have the right to:
      </p>
      <ul>
        <li>Access and update your personal information</li>
        <li>Request deletion of your personal information</li>
        <li>Object to processing of your personal information</li>
        <li>Request data portability</li>
        <li>Withdraw consent where processing is based on consent</li>
      </ul>
    </section>

    <section className={styles.section}>
      <h3>6. International Data Transfers</h3>
      <p>
        Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in accordance with applicable data protection laws.
      </p>
    </section>

    <section className={styles.section}>
      <h3>7. Data Retention</h3>
      <p>
        We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
      </p>
    </section>

    <section className={styles.section}>
      <h3>8. Contact Us</h3>
      <p>
        If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
      </p>
      <ul>
        <li>Email: privacy@trakvu.com</li>
        <li>Address: SmartTech ProServe LLC</li>
      </ul>
    </section>
  </div>
);

export default FullTermsModal; 